import { Box, Button, Dialog, DialogContent, DialogTitle, Skeleton, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { GetUserProfile } from '../../../services/User';
import { Colors } from '../../../styles/Variables';
import JustpointLogoIcon from '../../../assets/images/icon_logo_black.png';
import { removeUserDataLocalStorage } from '../../../utils/localStorage';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '../../../lib/avatar';
import { useRecoilValue } from 'recoil';
import sidebarState from '../../../stores/atoms/sidebarAtom';

export default function UserData(): JSX.Element {
    const [showDialog, setShowDialog] = useState(false);
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const sidebarOpen = useRecoilValue(sidebarState);

    const { data: user, isLoading } = useQuery(['userProfile'], () => GetUserProfile(), {
        retry: false
    });

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center'
                }}
            >
                {isLoading ? (
                    <>
                        <Skeleton variant="circular" width={46} height={46} sx={{ marginRight: '10px' }} />
                        <Box>
                            <Skeleton variant="text" width={120} />
                            <Skeleton variant="text" width={130} />
                        </Box>
                    </>
                ) : (
                    <div className="flex gap-2">
                        <Avatar
                            className={`h-11 w-11 ${sidebarOpen ? 'ml-0' : 'ml-1'}`}
                            onDoubleClick={() => setShowDialog(true)}
                        >
                            <AvatarImage src={user?.photo} />
                            <AvatarFallback className="text-sm">
                                {user?.first_name[0]}
                                {user?.last_name[0]}
                            </AvatarFallback>
                        </Avatar>
                        {sidebarOpen && (
                            <div className="flex flex-col gap-1">
                                <p className="text-sm w-32 truncate">{user?.full_name}</p>
                                <p className="text-grey-400 text-xs w-32 truncate">{user?.email}</p>
                            </div>
                        )}
                    </div>
                )}
            </Box>
            <Dialog open={showDialog} onClose={() => setShowDialog(false)} aria-labelledby="alert-dialog-title">
                <DialogTitle id="alert-dialog-title">
                    <Box
                        component="img"
                        sx={{
                            height: 44 * 0.55,
                            width: 38 * 0.55,
                            marginTop: '10px'
                        }}
                        alt="Justpoint Logo"
                        src={JustpointLogoIcon}
                    />
                </DialogTitle>

                <DialogContent>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexDirection: 'column',
                            minWidth: '372px',
                            gap: '25px'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px'
                            }}
                        >
                            <Avatar className="h-11 w-11">
                                <AvatarImage src={user?.photo} />
                                <AvatarFallback className="text-sm">
                                    {user?.first_name[0]}
                                    {user?.last_name[0]}
                                </AvatarFallback>
                            </Avatar>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        color: Colors.black,
                                        fontWeight: 500
                                    }}
                                >
                                    {user?.full_name}
                                </Typography>
                            </Box>
                        </Box>
                        <Typography
                            sx={{
                                fontSize: '18px',
                                fontWeight: 500,
                                color: Colors.greyishBrown
                            }}
                        >
                            Do you want to log out?
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '20px',
                                marginTop: '12px',
                                paddingBottom: '25px'
                            }}
                        >
                            <Button onClick={() => setShowDialog(false)} variant="outlined" sx={{ minWidth: '122px' }}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                sx={{ minWidth: '122px' }}
                                onClick={() => {
                                    removeUserDataLocalStorage();
                                    queryClient.invalidateQueries();
                                    navigate('/login');
                                }}
                                autoFocus
                            >
                                Log out
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}
